import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OwlMascot } from "@/components/OwlMascot";
import { toast } from "sonner";
import { UserPlus, GraduationCap, User } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const Signup = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("student");
  const [loading, setLoading] = useState(false);
  const { signupEmail } = useAuth();
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await signupEmail(name, email, password, role);
      toast.success(`Welcome to BrimiAI, ${u.name}! 🦉`);
      navigate(u.role === "teacher" ? "/teacher" : "/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col items-center justify-center bg-gradient-to-br from-cyan-500 to-blue-700 text-white p-12 relative overflow-hidden">
        <div className="glow-blob bg-amber-300 w-80 h-80 -top-16 -right-16" />
        <div className="glow-blob bg-emerald-400 w-80 h-80 -bottom-16 -left-16" />
        <div className="relative z-10 text-center max-w-md">
          <OwlMascot size={220} talking />
          <h2 className="font-outfit font-black text-4xl mt-6">Join the lab.</h2>
          <p className="mt-3 text-cyan-100 text-lg">Free access to Physics, Chemistry, and Biology experiments. Guided by an AI owl.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="text-sm text-slate-500 hover:text-slate-900 font-mono">← back</Link>
          <h1 className="font-outfit font-black text-4xl text-slate-900 mt-6" data-testid="signup-title">Create account</h1>
          <p className="mt-2 text-slate-600">Takes 20 seconds.</p>

          <Button
            type="button"
            variant="outline"
            className="w-full mt-8 h-12 rounded-full border-slate-300 hover:bg-white hover:-translate-y-0.5 transition-transform"
            onClick={googleLogin}
            data-testid="signup-google-btn"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-xs uppercase tracking-widest text-slate-400 font-bold">or</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setRole("student")}
                data-testid="signup-role-student"
                className={`flex flex-col items-start p-4 rounded-2xl border-2 transition-all ${role === "student" ? "border-blue-600 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
              >
                <User className={`h-5 w-5 mb-2 ${role === "student" ? "text-blue-600" : "text-slate-500"}`} />
                <div className="font-outfit font-bold text-slate-900">Student</div>
                <div className="text-xs text-slate-500 mt-1">Do experiments · earn XP</div>
              </button>
              <button
                type="button"
                onClick={() => setRole("teacher")}
                data-testid="signup-role-teacher"
                className={`flex flex-col items-start p-4 rounded-2xl border-2 transition-all ${role === "teacher" ? "border-blue-600 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
              >
                <GraduationCap className={`h-5 w-5 mb-2 ${role === "teacher" ? "text-blue-600" : "text-slate-500"}`} />
                <div className="font-outfit font-bold text-slate-900">Teacher</div>
                <div className="text-xs text-slate-500 mt-1">Assign · monitor · review</div>
              </button>
            </div>

            <div>
              <Label htmlFor="name">Name</Label>
              <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="mt-1.5 h-11" data-testid="signup-name-input" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@school.edu" className="mt-1.5 h-11" data-testid="signup-email-input" />
            </div>
            <div>
              <Label htmlFor="pwd">Password</Label>
              <Input id="pwd" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 chars" className="mt-1.5 h-11" data-testid="signup-password-input" />
            </div>
            <Button type="submit" className="w-full h-12 rounded-full bg-blue-600 hover:bg-blue-700 hover:-translate-y-0.5 transition-transform" disabled={loading} data-testid="signup-submit-btn">
              <UserPlus className="mr-2 h-4 w-4" />
              {loading ? "Creating..." : "Create account"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-slate-600 text-center">
            Already have one?{" "}
            <Link to="/login" className="text-blue-600 font-semibold hover:underline" data-testid="signup-to-login-link">Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
